// /ops/brokers/$brokerId — one node's subtree drill-down (SM-725,
// SM-621 phase 08d).
//
// ops-api /admin/ops/brokers/{id}/conservation: the node and every
// descendant, one row per node × currency, each with its conservation
// terms (pool, allocations outstanding, revenue, payout reservation,
// signed clearing, collateral, exposure / limit / headroom), its
// lifecycle badge and its open findings — plus the open findings of the
// whole subtree. Read-only; recovery is the finding's approved action.
//
// In the currencies the node's root trades (SM-870): a broker tree reads
// GOLD alone, so the currency is shown rather than chosen; the
// SYSTEM_BROKER's offers all three, every currency by default.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { FixedCurrency } from "@/components/brokers/FixedCurrency";
import { SYSTEM_BROKER_ID } from "@/components/brokers/fees";
import { isBrokerCurrency } from "@/components/brokers/governance";
import { useNodeCurrencies } from "@/components/brokers/useNodeCurrencies";
import { CanonAmount, LifecycleBadge, clearingDirectionLabel } from "@/components/ops/brokerRecon";
import { msToIso } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtDate, fmtRelative } from "@/lib/format";
import { fetchBrokerConservation, fetchFindings, opsKeys } from "@/lib/ops-queries";
import { BROKER_MONEY_TASK_TYPES, severityLabel, type BrokerPosition, type Finding } from "@/lib/ops-types";

interface Search {
  currency?: string;
}

export const Route = createFileRoute("/_app/ops/brokers/$brokerId")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    currency: isBrokerCurrency(s.currency) ? s.currency : undefined,
  }),
  component: BrokerSubtreePage,
});

const th = "px-3 py-2.5 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-2 text-xs";

function BrokerSubtreePage() {
  const { brokerId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // The node's path names its root; the root's path read names what the
  // tree trades.
  const node = useNodeCurrencies(brokerId);
  const rootId = node.rootBrokerId ?? brokerId;
  const { currencies } = useNodeCurrencies(rootId, rootId === SYSTEM_BROKER_ID);
  const single = currencies.length === 1;
  const currency = single
    ? currencies[0]
    : search.currency && (currencies as readonly string[]).includes(search.currency)
      ? search.currency
      : undefined;
  const q = useQuery({
    queryKey: opsKeys.brokerConservation(brokerId, currency),
    queryFn: () => fetchBrokerConservation(brokerId, currency),
    refetchInterval: 60_000,
  });
  const fparams = { task_type: BROKER_MONEY_TASK_TYPES, subtree_broker_id: brokerId, only_open: true, limit: 50 };
  const fq = useQuery({ queryKey: opsKeys.findings(fparams), queryFn: () => fetchFindings(fparams) });

  const rows = q.data?.items ?? [];
  const self = rows.find((r) => r.broker_id === brokerId);
  const nodes = groupByNode(rows);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Link to="/ops/brokers" className="text-2xs text-muted hover:underline">
            ← Broker integrity
          </Link>
          <h2 className="font-display text-xl tracking-tight">
            {self?.display_name || brokerId}{" "}
            {self ? <LifecycleBadge state={self.lifecycle_state} /> : null}
          </h2>
          {self ? (
            <p className="font-mono text-2xs text-muted">{self.path.join(" › ")}</p>
          ) : null}
        </div>
        <div className="ml-auto w-40">
          {single ? (
            <FixedCurrency currency={currencies[0]} />
          ) : (
            <Select
              aria-label="Currency"
              value={currency ?? ""}
              onChange={(e) => navigate({ search: (s) => ({ ...s, currency: e.target.value || undefined }) })}
              placeholder="Every currency"
              options={currencies.map((c) => ({ value: c, label: c }))}
            />
          )}
        </div>
      </div>

      {q.isLoading ? <Skeleton className="h-40" /> : null}
      {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
      {q.data && rows.length === 0 ? (
        <EmptyState title="Not in the reconciliation snapshot" description="The next broker-recon sweep writes it." />
      ) : null}

      {nodes.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Subtree</CardTitle>
            {q.data?.computed_at_unix_ms ? (
              <span className="text-2xs text-muted" title="When the broker reconciliation last wrote this snapshot">
                {fmtRelative(msToIso(q.data.computed_at_unix_ms) ?? "")}
              </span>
            ) : null}
          </CardHeader>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Node</th>
                    <th className={th}>State</th>
                    <th className={th}>Currency</th>
                    <th className={`${th} text-right`}>Pool (avail + reserved + pending)</th>
                    <th className={`${th} text-right`}>Allocated to users</th>
                    <th className={`${th} text-right`}>Revenue (pending / available)</th>
                    <th className={`${th} text-right`}>Payout reserved</th>
                    <th className={`${th} text-right`}>Clearing</th>
                    <th className={`${th} text-right`}>Collateral</th>
                    <th className={`${th} text-right`}>Exposure</th>
                    <th className={`${th} text-right`}>Headroom</th>
                    <th className={`${th} text-right`}>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {nodes.flatMap(({ node, rows: nrows }) =>
                    nrows.map((r, i) => <SubtreeRow key={`${node}:${r.currency}`} r={r} first={i === 0} />),
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Open findings in this subtree</CardTitle>
          <Link
            to="/ops/findings"
            search={{ subtree_broker_id: brokerId, only_open: "true" }}
            className="text-2xs text-neon hover:underline"
          >
            Open in the inbox
          </Link>
        </CardHeader>
        <CardBody className="p-0">
          {fq.isLoading ? <Skeleton className="m-5 h-12" /> : null}
          {fq.isError ? <ErrorPanel error={fq.error} reset={() => void fq.refetch()} /> : null}
          {fq.data && fq.data.Items.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-muted">No open broker, exposure or clearing finding.</p>
          ) : null}
          {fq.data && fq.data.Items.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={`${th} px-5`}>Finding</th>
                  <th className={th}>Severity</th>
                  <th className={th}>Node</th>
                  <th className={th}>Currency</th>
                  <th className={th}>Operation</th>
                  <th className={th}>Last detected</th>
                </tr>
              </thead>
              <tbody>
                {fq.data.Items.map((f) => (
                  <tr key={f.finding_id} className="border-b border-border/60 last:border-0">
                    <td className={`${td} px-5`}>
                      <Link to="/ops/findings/$id" params={{ id: f.finding_id }} className="font-mono text-2xs hover:underline">
                        {f.finding_code}
                      </Link>
                    </td>
                    <td className={td}>
                      <Badge tone={f.severity === 1 ? "danger" : f.severity === 2 ? "warning" : "muted"}>{severityLabel(f.severity)}</Badge>
                    </td>
                    <td className={`${td} font-mono text-2xs`}>{findingBroker(f)}</td>
                    <td className={td}>{findingCurrency(f) ? <CurrencyBadge currency={findingCurrency(f)} /> : null}</td>
                    <td className={`${td} font-mono text-2xs`}>{findingOperation(f)}</td>
                    <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(f.last_detected_at_unix_ms))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

function groupByNode(rows: BrokerPosition[]): { node: string; rows: BrokerPosition[] }[] {
  const out: { node: string; rows: BrokerPosition[] }[] = [];
  for (const r of rows) {
    const last = out[out.length - 1];
    if (last && last.node === r.broker_id) last.rows.push(r);
    else out.push({ node: r.broker_id, rows: [r] });
  }
  return out;
}

function SubtreeRow({ r, first }: { r: BrokerPosition; first: boolean }) {
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className={`${td} px-5`}>
        {first ? (
          <span style={{ paddingLeft: `${Math.max(0, r.depth) * 16}px` }}>
            <Link to="/ops/brokers/$brokerId" params={{ brokerId: r.broker_id }} className="hover:underline">
              {r.display_name || r.broker_id}
            </Link>
          </span>
        ) : null}
      </td>
      <td className={td}>{first ? <LifecycleBadge state={r.lifecycle_state} /> : null}</td>
      <td className={td}>
        <CurrencyBadge currency={r.currency} />
      </td>
      <td className={`${td} text-right`}>
        <CanonAmount currency={r.currency} value={r.pool_total} />
      </td>
      <td className={`${td} text-right`}>
        <CanonAmount currency={r.currency} value={r.allocated_outstanding} />
      </td>
      <td className={`${td} text-right`}>
        <span className="inline-flex items-center gap-1">
          <CanonAmount currency={r.currency} value={r.revenue_pending} />
          <span className="text-muted">/</span>
          <CanonAmount currency={r.currency} value={r.revenue_available} />
        </span>
      </td>
      <td className={`${td} text-right`}>
        <CanonAmount currency={r.currency} value={r.payout_reserved} />
      </td>
      <td className={`${td} text-right`} title={clearingDirectionLabel(r.clearing_direction)}>
        {r.clearing_balance !== undefined ? (
          <span className="inline-flex flex-col items-end">
            <CanonAmount currency={r.currency} value={r.clearing_balance} tone="auto" />
            <span className="text-2xs text-muted">{clearingDirectionLabel(r.clearing_direction)}</span>
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className={`${td} text-right`}>
        <CanonAmount currency={r.currency} value={r.collateral} />
      </td>
      <td className={`${td} text-right`}>{r.exposure_known ? <CanonAmount currency={r.currency} value={r.exposure} /> : "—"}</td>
      <td className={`${td} text-right`}>
        {r.exposure_known && r.has_limit ? (
          <span className="inline-flex flex-col items-end">
            <CanonAmount currency={r.currency} value={r.headroom} />
            <span className="text-2xs text-muted" title="Limit used">
              {pct(r.utilisation_bps)}
            </span>
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className={`${td} text-right`}>
        {r.open_findings > 0 ? (
          <Badge tone={r.open_critical > 0 ? "danger" : "warning"}>{r.open_findings}</Badge>
        ) : (
          <span className="text-muted">0</span>
        )}
      </td>
    </tr>
  );
}

const DETAIL_BROKER = "broker_id";
const DETAIL_ROOT = "root_broker_id";
const DETAIL_CURRENCY = "currency";
const DETAIL_OPERATION = "operation_type";

function detailOf(f: Finding, key: string): string {
  const v = f.details?.[key];
  return typeof v === "string" ? v : "";
}

function findingBroker(f: Finding): string {
  return detailOf(f, DETAIL_BROKER) || detailOf(f, DETAIL_ROOT);
}

function findingCurrency(f: Finding): string {
  return detailOf(f, DETAIL_CURRENCY);
}

function findingOperation(f: Finding): string {
  return detailOf(f, DETAIL_OPERATION);
}

function pct(bps: number): string {
  return `${(bps / 100).toFixed(1)}%`;
}
